import React from "react";
import { useNavigate } from "react-router-dom";
import DashLayout from "@/components/DashLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { IdCard, FileText, Award, Printer, ArrowRight } from "lucide-react";

/**
 * A single, shared bulk-actions hub that lists ID Card / Admit Card / TC / Certificate
 * flows and links to the Students register where the per-student print/issue actions
 * are already implemented. Reuses existing plumbing instead of duplicating PDF logic.
 */
export default function BulkDocsHub({ kind }) {
  const nav = useNavigate();

  const CONFIG = {
    "id-cards": {
      title: "ID Cards",
      icon: IdCard,
      color: "bg-blue-50 text-blue-700",
      desc: "Bulk-print student ID cards with photo, class and QR code.",
      button: "Print icon per student",
      testid: "hub-idcards",
    },
    "admit-cards": {
      title: "Admit Cards",
      icon: Award,
      color: "bg-purple-50 text-purple-700",
      desc: "Print admit cards for the latest exam directly from the Students register.",
      button: "Award icon per student",
      testid: "hub-admitcards",
    },
    tc: {
      title: "Transfer Certificates",
      icon: FileText,
      color: "bg-emerald-50 text-emerald-700",
      desc: "Issue a Transfer Certificate for a student — TC number, reason, conduct and date of leaving.",
      button: "FileText icon per student",
      testid: "hub-tc",
    },
    certificates: {
      title: "Certificates",
      icon: Award,
      color: "bg-amber-50 text-amber-700",
      desc: "Bonafide / Character / Study certificates — generate directly from the printable ID card and TC dialogs.",
      button: "TC + iCard actions per student",
      testid: "hub-cert",
    },
    "report-cards": {
      title: "Report Cards",
      icon: Printer,
      color: "bg-indigo-50 text-indigo-700",
      desc: "Auto-generated report cards using grades from Marks Entry and attendance %.",
      button: "Print icon per student",
      testid: "hub-report",
    },
  };
  const cfg = CONFIG[kind] || CONFIG["id-cards"];
  const Icon = cfg.icon;

  return (
    <DashLayout variant="school" title={cfg.title}>
      <Card className="p-6 max-w-2xl" data-testid={cfg.testid}>
        <div className={`w-14 h-14 rounded-xl ${cfg.color} flex items-center justify-center`}>
          <Icon className="w-7 h-7" />
        </div>
        <h2 className="text-xl font-bold mt-4">{cfg.title}</h2>
        <p className="text-sm text-slate-600 mt-2">{cfg.desc}</p>
        <div className="mt-4 text-xs text-slate-500 bg-slate-50 rounded p-3 border border-slate-200">
          <b>How to use:</b> Open the Students register, filter by class if needed, and click the
          <b> {cfg.button}</b> on any student row. The printable PDF opens in a new tab.
        </div>
        <Button className="mt-5 bg-blue-700 hover:bg-blue-800" onClick={() => nav("/school/students")} data-testid={`${cfg.testid}-open`}>
          Open Students Register <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </Card>
    </DashLayout>
  );
}
